import { useRouter } from '../../../../utils/helper'
import { Authorize, validateCorrectRole } from '../../../components/Authorize'
import { ROLE } from '../../../services/user/user-typed'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import Button from '../../../components/common/Button'
import {
  Grid,
  makeStyles,
  FormControlLabel,
  Modal,
  Card,
  CardContent,
  CardMedia,
} from '@material-ui/core'
import ConfirmCard from '../../../components/common/Card'
import Text from '../../../components/common/Text'
import { Field, Form } from 'react-final-form'
import { CheckBoxField, InputField } from '../../../components/fields'
import * as paths from '../../../constants/path'
import { useHistory } from 'react-router-dom'
import { cp, createValidation, maxLength, required } from '../../../../utils/field-validation'
import { useToastForReactQuery, useVisible } from '../../../../utils/custom-hooks'
import { UploadThumbNail } from './UploadThumbNail'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import DeleteIcon from '@material-ui/icons/Delete'
import { useCreateTag, useGetTagById, useUpdateTag } from '../../../services/tag/tag-query'
import { useTranslation } from 'react-i18next'
import { useCurrentUser } from '../../../services/user/user-query'

const ButtonStyled = styled(Button)`
  padding: 7px 13px;
`

const CustomCard = styled(ConfirmCard)`
  width: 100%;
  max-width: 1000px;
`

const useStyles = makeStyles(theme => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

const ContentLayout = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 24px;
`

const GridUploadArea = styled(Grid)`
  padding: 8px 16px 8px 0;
  border-radius: 4px;
  border: 1px dotted gray;
  :hover {
    background-color: #dadada;
  }
`

const MarginTopButton = styled.div`
  margin-top: 86px;
  margin-left: 24px;
`

type FormValue = {
  name: string
  tagKey: string
  status: boolean
  activeImage: string
  inactiveImage: string
  rank?: number
}

const validate = createValidation<FormValue>(values => ({
  name: cp(required('required tag name'), maxLength(50, 'Maximum length required 50')),
  tagKey: cp(required('required tag key')),
  activeImage: cp(required('required active image')),
  inactiveImage: cp(required('required inactive image')),
}))

const TagDetail = () => {
  const { query } = useRouter()
  const [isViewMode, setIsViewMode] = useState(false)
  const { data: currentUser } = useCurrentUser()
  const { t } = useTranslation()
  const history = useHistory()
  const toast = useToastForReactQuery()
  const onConfirmModalVisible = useVisible()
  const { data: tag } = useGetTagById(query.id)
  // const onDeleteModalVisible = useVisible()
  const { mutate: createTag } = useCreateTag()
  const { mutate: updateTag } = useUpdateTag(query.id)
  const classes = useStyles()

  useEffect(() => {
    if (tag?.id || !validateCorrectRole(ROLE.MANAGER, currentUser?.role)) {
      setIsViewMode(true)
    }
  }, [currentUser, tag])

  const onGoBackToTag = useCallback(() => {
    history.push(paths.tag())
  }, [history])

  const handleOpenConfirmModal = useCallback(() => {
    onConfirmModalVisible.open()
  }, [onConfirmModalVisible])

  const handleCloseConfirmModal = useCallback(() => {
    onConfirmModalVisible.close()
  }, [onConfirmModalVisible])

  // const handleOpenDeleteModal = useCallback(() => {
  //   onDeleteModalVisible.open()
  // }, [onDeleteModalVisible])

  // const handleCloseDeleteModal = useCallback(() => {
  //   onDeleteModalVisible.close()
  // }, [onDeleteModalVisible])

  const initialValues = useMemo(() => {
    return query.id
      ? {
          name: tag?.name,
          tagKey: tag?.tagKey,
          status: tag?.status,
          activeImage: tag?.activeImage,
          inactiveImage: tag?.inactiveImage,
          rank: tag?.rank,
        }
      : {
          status: false,
        }
  }, [query.id, tag])

  const onSubmit = useCallback(
    value => {
      if (query.id) {
        updateTag(value, {
          onSuccess: () => {
            toast.open('Success', {
              type: 'success',
              autoClose: 3 * 1000,
            })
            handleCloseConfirmModal()
            onGoBackToTag()
          },
          onError: (error: any) => {
            toast.open(t(`${error}`), {
              type: 'error',
              autoClose: 3 * 1000,
            })
            handleCloseConfirmModal()
          },
        })
      } else {
        console.debug('create', value)
        createTag(value, {
          onSuccess: () => {
            toast.open('Success', {
              type: 'success',
              autoClose: 3 * 1000,
            })
            handleCloseConfirmModal()
            onGoBackToTag()
          },
          onError: (error: any) => {
            toast.open(t(`${error}`), {
              type: 'error',
              autoClose: 3 * 1000,
            })
            handleCloseConfirmModal()
          },
        })
      }
    },
    [createTag, handleCloseConfirmModal, onGoBackToTag, query.id, t, toast, updateTag],
  )

  const onClickEdit = useCallback(() => {
    setIsViewMode(false)
  }, [])

  // const onClickDelete = useCallback(() => {
  //   //delete tag
  //   onGoBackToTag()
  // }, [onGoBackToTag])

  return (
    <>
      <Authorize role={ROLE.ADMIN}>
        {query.id && (
          <div
            style={{
              marginBottom: '16px',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <ButtonStyled
              variant="warning"
              style={{
                marginRight: '16px',
              }}
              onClick={onClickEdit}
            >
              Edit
            </ButtonStyled>
            {/* <ButtonStyled variant="danger" onClick={handleOpenDeleteModal}>
              Delete
            </ButtonStyled> */}
          </div>
        )}
      </Authorize>
      <CustomCard>
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          validate={validate}
          render={({ handleSubmit, invalid, values, form }) => {
            return (
              <>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
                  <div style={{ width: '70%', display: 'flex', flexDirection: 'column' }}>
                    <ContentLayout>
                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Name</Text>
                        <Field
                          name="name"
                          component={InputField}
                          placeholder="tag name"
                          id="name"
                          disabled={isViewMode}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <div style={{ paddingBottom: '10px' }}>
                        <Text>Tag key</Text>
                        <Field
                          name="tagKey"
                          component={InputField}
                          placeholder="tag key"
                          id="key"
                          disabled={isViewMode}
                          style={{ width: '100%' }}
                        />
                      </div>

                      <FormControlLabel
                        control={
                          <Field name="status" component={CheckBoxField} disabled={isViewMode} />
                        }
                        label="Active"
                      />

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          {!isViewMode && (
                            <div style={{ paddingBottom: '10px', cursor: 'pointer' }}>
                              <Text>Active Image</Text>
                              {!values.activeImage ? (
                                <UploadThumbNail
                                  onChange={value => {
                                    form?.change('activeImage', value)
                                  }}
                                />
                              ) : (
                                <GridUploadArea
                                  container
                                  alignItems="center"
                                  justify="space-between"
                                >
                                  <div>
                                    <AttachFileIcon
                                      width={40}
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                    Uploaded
                                  </div>
                                  <DeleteIcon
                                    style={{ color: 'red', width: '24px', height: '24px' }}
                                    onClick={() => {
                                      form?.change('activeImage', null)
                                    }}
                                  />
                                </GridUploadArea>
                              )}
                            </div>
                          )}
                          <div
                            style={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}
                          >
                            {!!values?.activeImage && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {isViewMode && <Text>Active Image</Text>}
                                <Card
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                  }}
                                >
                                  {values.activeImage.startsWith('data:image/svg+xml') ? (
                                    (() => {
                                      const decodedSvg = atob(values.activeImage.split(',')[1])
                                      const sizedSvg = decodedSvg
                                        // Replace width or add if missing
                                        .replace(/(<svg[^>]*?)\swidth=".*?"/, '$1')
                                        .replace(/(<svg[^>]*?)\sheight=".*?"/, '$1')
                                        .replace(/<svg/, '<svg width="80" height="80"')
                                      return (
                                        <div
                                          style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                          }}
                                          dangerouslySetInnerHTML={{ __html: sizedSvg }}
                                        />
                                      )
                                    })()
                                  ) : (
                                    <CardMedia
                                      component="img"
                                      image={values.activeImage}
                                      alt="active_image"
                                      style={{
                                        width: 80,
                                        height: 80,
                                      }}
                                    />
                                  )}
                                  <CardContent style={{ maxWidth: 320, padding: 8 }}>
                                    <Text variant="subtitle1" style={{ textAlign: 'center' }}>
                                      {values?.name}
                                    </Text>
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </div>
                        </div>

                        <div>
                          {!isViewMode && (
                            <div style={{ paddingBottom: '10px', cursor: 'pointer' }}>
                              <Text>Inactive Image</Text>
                              {!values.inactiveImage ? (
                                <UploadThumbNail
                                  onChange={value => {
                                    form?.change('inactiveImage', value)
                                  }}
                                />
                              ) : (
                                <GridUploadArea
                                  container
                                  alignItems="center"
                                  justify="space-between"
                                >
                                  <div>
                                    <AttachFileIcon
                                      width={40}
                                      style={{ width: '24px', height: '24px' }}
                                    />
                                    Uploaded
                                  </div>
                                  <DeleteIcon
                                    style={{ color: 'red', width: '24px', height: '24px' }}
                                    onClick={() => {
                                      form?.change('inactiveImage', null)
                                    }}
                                  />
                                </GridUploadArea>
                              )}
                            </div>
                          )}
                          <div
                            style={{ margin: '16px 0', display: 'flex', justifyContent: 'center' }}
                          >
                            {!!values?.inactiveImage && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {isViewMode && <Text>Inactive Image</Text>}
                                <Card
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexDirection: 'column',
                                  }}
                                >
                                  {values?.inactiveImage.startsWith('data:image/svg+xml') ? (
                                    (() => {
                                      const decodedSvg = atob(values.inactiveImage.split(',')[1])
                                      const sizedSvg = decodedSvg
                                        // Replace width or add if missing
                                        .replace(/(<svg[^>]*?)\swidth=".*?"/, '$1')
                                        .replace(/(<svg[^>]*?)\sheight=".*?"/, '$1')
                                        .replace(/<svg/, '<svg width="80" height="80"')
                                      return (
                                        <div
                                          style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                          }}
                                          dangerouslySetInnerHTML={{ __html: sizedSvg }}
                                        />
                                      )
                                    })()
                                  ) : (
                                    <CardMedia
                                      component="img"
                                      width="100%"
                                      image={values.inactiveImage}
                                      alt="inactive_image"
                                      // style={{
                                      //   aspectRatio: '167 / 198',
                                      //   maxWidth: 320,
                                      // }}
                                      style={{
                                        width: 80,
                                        height: 80,
                                      }}
                                    />
                                  )}
                                  <CardContent style={{ maxWidth: 320, padding: 8 }}>
                                    <Text variant="subtitle1" style={{ textAlign: 'center' }}>
                                      {values?.name}
                                    </Text>
                                  </CardContent>
                                </Card>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </ContentLayout>

                    <Authorize role={ROLE.ADMIN}>
                      <MarginTopButton>
                        <Button onClick={handleOpenConfirmModal} disabled={invalid || isViewMode}>
                          Submit
                        </Button>
                      </MarginTopButton>
                    </Authorize>
                  </div>

                  {/* <IPhoneFrame>
                    <BannerWrapper>
                      <BannerContent>
                        <img
                          alt=""
                          src={bannerIcon}
                          style={{ height: '32px', width: '32px' }}
                        ></img>

                        <BannerText
                          dangerouslySetInnerHTML={{
                            __html: bodyValue,
                          }}
                        ></BannerText>
                      </BannerContent>
                      <CloseButton>×</CloseButton>
                    </BannerWrapper>
                  </IPhoneFrame> */}
                </div>

                <Modal
                  className={classes.modal}
                  open={onConfirmModalVisible.visible}
                  onClose={onConfirmModalVisible.close}
                >
                  <ConfirmCard
                    style={{
                      display: 'flex',
                      width: '300px',
                      padding: '30px',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Text>Confirm</Text>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignSelf: 'center',
                        paddingTop: '10px',
                      }}
                    >
                      <Button
                        style={{
                          alignSelf: 'center',
                          marginRight: '20px',
                        }}
                        onClick={handleSubmit}
                      >
                        Yes
                      </Button>
                      <Button
                        style={{
                          alignSelf: 'center',
                          backgroundColor: '#FF0000',
                          borderColor: '#FF0000',
                        }}
                        onClick={handleCloseConfirmModal}
                      >
                        No
                      </Button>
                    </div>
                  </ConfirmCard>
                </Modal>

                {/* <Modal
                  className={classes.modal}
                  open={onDeleteModalVisible.visible}
                  onClose={onDeleteModalVisible.close}
                >
                  <ConfirmCard
                    style={{
                      display: 'flex',
                      width: '300px',
                      padding: '30px',
                      alignItems: 'center',
                      textAlign: 'center',
                    }}
                  >
                    <Text>Confirm Delete</Text>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignSelf: 'center',
                        paddingTop: '10px',
                      }}
                    >
                      <Button
                        style={{
                          alignSelf: 'center',
                          marginRight: '20px',
                        }}
                        onClick={onClickDelete}
                      >
                        Yes
                      </Button>
                      <Button
                        style={{
                          alignSelf: 'center',
                          backgroundColor: '#FF0000',
                          borderColor: '#FF0000',
                        }}
                        onClick={handleCloseDeleteModal}
                      >
                        No
                      </Button>
                    </div>
                  </ConfirmCard>
                </Modal> */}
              </>
            )
          }}
        />
      </CustomCard>
    </>
  )
}

export default TagDetail
